import React from "react";

interface SelectablePillsProps {
  title?: string;
  options: string[];
  selectedOptions: string[];
  onSelectionChange: (selected: string[]) => void;
  disabled?: boolean;
  showSelectAll?: boolean;
}

const SelectablePills: React.FC<SelectablePillsProps> = ({
  title,
  options,
  selectedOptions,
  onSelectionChange,
  disabled = false,
  showSelectAll = false,
}) => {
  const handleToggleOption = (option: string) => {
    if (disabled) return;

    const isSelected = selectedOptions.includes(option);
    let newSelection: string[];

    if (isSelected) {
      newSelection = selectedOptions.filter((o) => o !== option);
    } else {
      newSelection = [...selectedOptions, option];
    }

    onSelectionChange(newSelection);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onSelectionChange(options);
  };

  const handleClearAll = () => {
    if (disabled) return;
    onSelectionChange([]);
  };

  return (
    <div className="tw-space-y-4">
      {/* Title */}
      {title && (
        <h3 className="tw-text-base tw-font-medium tw-text-neutral-800">
          {title}
        </h3>
      )}

      {/* Select All/Clear All controls */}
      {showSelectAll && (
        <div className="tw-flex tw-gap-2 tw-justify-end">
          <button
            onClick={handleSelectAll}
            disabled={disabled || selectedOptions.length === options.length}
            className="tw-text-sm tw-text-neutral-600 hover:tw-text-neutral-800 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            Select All
          </button>
          <span className="tw-text-neutral-400">•</span>
          <button
            onClick={handleClearAll}
            disabled={disabled || selectedOptions.length === 0}
            className="tw-text-sm tw-text-neutral-600 hover:tw-text-neutral-800 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            Clear All
          </button>
        </div>
      )}

      {/* Pills container - using PlanStep styling */}
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-overflow-y-auto tw-max-h-[60vh] sm:tw-max-h-[50vh]">
        {options.map((option) => {
          const isSelected = selectedOptions.includes(option);

          return (
            <button
              key={option}
              onClick={() => handleToggleOption(option)}
              disabled={disabled}
              className={`tw-px-3 tw-py-2 tw-rounded-full tw-text-sm tw-border tw-transition-all ${
                isSelected
                  ? "tw-bg-neutral-800 tw-text-white tw-border-neutral-800"
                  : "tw-bg-neutral-800/5 tw-text-neutral-800 tw-border-neutral-800/30 hover:tw-border-neutral-800/60"
              } ${
                disabled
                  ? "tw-opacity-50 tw-cursor-not-allowed"
                  : "tw-cursor-pointer"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {options.length === 0 && (
        <div className="tw-text-center tw-py-8 tw-text-neutral-500">
          <p>No options available.</p>
        </div>
      )}
    </div>
  );
};

export default SelectablePills;
